// ============================================================
// TargetIdentityHeader - the threat assessment (v8.255.0).
//
// The target panel's face: not a nameplate but a SENSOR RETURN.
// Deliberately a FORK of PanelIdentityHeader, not a parameter on
// it (see the ADR). One grammar since v8.264.0: identity row,
// verdict band (FEAR is a danger-tinted note chip), the vitals
// strip, then a single data band of labelled fact-columns. The
// LIVE combat readouts (reaction odds, cover/stance/facing, force
// field) moved to the scan HUD - this header keeps who / what /
// how dangerous. The chip rail is gone on the target side; the
// actor's header keeps its own.
//
// Contract kept for e2e/panel-identity.spec.js: root testid
// panel-identity, eyebrow DOM text contains "Targeting",
// aria-labels "Select target" / "Previous target" / "Next target".
// Stencil caps come from CSS text-transform, never DOM strings.
//
// All readout math is projection-fed and render-only: the relation
// facts (range) are per-attacker and must NEVER be written back.
// ============================================================
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import clsx from '../utils/clsx';
import CombatantVitals from './CombatantVitals';
import { classifyThreat, soakOf, armourSpread } from '../engine/threatAssessment';
import { RANGE_MODS, RANGE_BAND_LABELS } from '../engine/attackCalc';
import { effectiveVehicleFacing, VEHICLE_FACING_LABELS } from '../engine/vehicleFacing';
import { condType } from '../engine/conditions';
import type { Target } from '../types';
import styles from '../styles/TargetIdentityHeader.module.css';

export interface TargetIdentityHeaderProps {
  name: string;
  /** Projected defender - relation facts (cover, facing) already folded in. */
  target?: Target | null;
  /** The sheet, for parsed weapons and psy (classifyThreat v2). */
  sourceCharacter?: unknown;
  onNameClick: () => void;
  expanded?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  /** The Templates menu, unchanged from the shared header days. */
  trailing?: ReactNode;
  /** The viewer's live map line to this target. Render-only. */
  mapMetres?: number | null;
  rangeBandFromMap?: string | null;
  /** The scan flip (v8.269.0): the denomination is a button. */
  codexOpen?: boolean;
  onToggleCodex?: () => void;
}

const signed = (v: number) => (v > 0 ? `+${v}` : String(v));

const COVER_WORDS: Record<string, string> = { low: 'LOW', half: 'HALF', threeQuarter: '¾', full: 'FULL' };

/** "¾ 4" when in cover, else null. */
function coverFact(target: Target): string | null {
  const level = target.cover?.level;
  if (!level || level === 'none') return null;
  return `${COVER_WORDS[level] ?? level.toUpperCase()} ${target.cover?.ap ?? 0}`;
}

/** A vehicle's presented facing, else null. */
function facingFact(target: Target): string | null {
  if (target.bodyType !== 'vehicle') return null;
  return (VEHICLE_FACING_LABELS[effectiveVehicleFacing(target) ?? 'front'] || '').toUpperCase();
}

/**
 * Who / what / how dangerous, and the cross-tab facts (v8.272.1): cover,
 * stance and facing return to the header as fact-columns when ACTIVE - the
 * FIELD precedent. Cheap analytical calls only: this header re-renders on
 * attacker churn (accepted ADR) and must never run a Monte Carlo.
 */
function readoutOf(target: Target, sourceCharacter: unknown) {
  const ff = target.forceField;
  return {
    threat: classifyThreat(target, sourceCharacter as never),
    soak: soakOf(target),
    spread: armourSpread(target),
    field: ff?.active && (ff?.protectionRating ?? 0) > 0 ? ff : null,
    cover: coverFact(target),
    stance: (target.conditions || []).some((c) => condType(c) === 'Ds'),
    facing: facingFact(target),
  };
}

/** "12m · SHORT (+10)", or BEYOND with no band. The rails read the same fact (plan design-gaps, L7). */
export function rangeFact(metres: number, band: string | null): string {
  if (!band) return `${Math.round(metres)}m · BEYOND`;
  return `${Math.round(metres)}m · ${(RANGE_BAND_LABELS[band] || band).toUpperCase()} (${signed(RANGE_MODS[band] ?? 0)})`;
}

export default function TargetIdentityHeader({
  name,
  target = null,
  sourceCharacter = null,
  onNameClick,
  expanded = false,
  onPrev,
  onNext,
  trailing,
  mapMetres = null,
  rangeBandFromMap = null,
  codexOpen = false,
  onToggleCodex,
}: TargetIdentityHeaderProps) {
  // The live combat readouts (odds, chips) live on the scan HUD since
  // v8.264.0; the header keeps who / what / how dangerous.
  const readout = useMemo(() => (target ? readoutOf(target, sourceCharacter) : null), [target, sourceCharacter]);
  return (
    <div className={styles.header} data-testid="panel-identity">
      <div className={styles.topRow}>
        <span className={styles.eyebrow}>
          <span aria-hidden="true">{'⌖ '}</span>
          Targeting
        </span>
        <span className={styles.controls}>
          {onPrev && (
            <button type="button" className={styles.cycle} aria-label="Previous target" onClick={onPrev}>
              ◀
            </button>
          )}
          {onNext && (
            <button type="button" className={styles.cycle} aria-label="Next target" onClick={onNext}>
              ▶
            </button>
          )}
          {trailing}
        </span>
      </div>
      <button
        type="button"
        className={styles.name}
        aria-label="Select target"
        aria-expanded={expanded}
        aria-haspopup="listbox"
        onClick={onNameClick}
      >
        <span className={styles.nameText}>{name}</span>
        <span className={styles.caret} aria-hidden="true">
          ▾
        </span>
      </button>
      {readout && <ThreatVerdict threat={readout.threat} codexOpen={codexOpen} onToggleCodex={onToggleCodex} />}
      {target && <CombatantVitals target={target} layout="strip" showBar name={name} />}
      {readout && (
        <DataBand readout={readout} range={mapMetres != null ? rangeFact(mapMetres, rangeBandFromMap) : null} />
      )}
    </div>
  );
}

type Readout = ReturnType<typeof readoutOf>;

interface VerdictProps {
  threat: Readout['threat'];
  codexOpen: boolean;
  onToggleCodex?: () => void;
}

/**
 * The DENOMINATION is a button: it opens the threat codex - the scan's
 * other face (stencil look via CSS, not chrome). The epithets are
 * worst-first and capped; the codex carries the full set.
 */
function ThreatVerdict({ threat, codexOpen, onToggleCodex }: VerdictProps) {
  return (
    <div className={styles.verdict} data-testid="target-threat-class">
      <button
        type="button"
        className={styles.denomination}
        data-testid="target-denomination"
        aria-label="Open threat codex"
        aria-expanded={codexOpen}
        onClick={onToggleCodex}
      >
        {threat.label}
      </button>
      <span className={styles.fact}>{threat.sizeLabel}</span>
      {threat.epithets.slice(0, 4).map((n) => (
        <span key={n} className={clsx(styles.noteChip, n.startsWith('FEAR') && styles.noteChipDanger)}>
          {n}
        </span>
      ))}
    </div>
  );
}

/** ONE data band of labelled fact-columns: micro-label over value, the same grammar for every fact. */
function DataBand({ readout, range }: { readout: Readout; range: string | null }) {
  const { field } = readout;
  return (
    <div className={styles.dataBand}>
      <FactCol testId="target-soak" label="SOAK">
        TB {readout.soak} · {readout.spread}
      </FactCol>
      {field && (
        <FactCol testId="target-field" label="FIELD">
          {field.protectionRating} {'⚡'} {field.overloadThreshold}
        </FactCol>
      )}
      {readout.cover && (
        <FactCol testId="target-cover" label="COVER">
          {readout.cover}
        </FactCol>
      )}
      {readout.stance && (
        <FactCol testId="target-stance" label="STANCE">
          SET
        </FactCol>
      )}
      {readout.facing && (
        <FactCol testId="target-facing" label="FACING">
          {readout.facing}
        </FactCol>
      )}
      {range && (
        <FactCol testId="target-relation" label="RANGE">
          {range}
        </FactCol>
      )}
    </div>
  );
}

function FactCol({ testId, label, children }: { testId: string; label: string; children: ReactNode }) {
  return (
    <span className={styles.factCol} data-testid={testId}>
      <span className={styles.factLabel}>{label}</span>
      <span className={styles.factValue}>{children}</span>
    </span>
  );
}
